import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface AutoPoConfig {
  id: string;
  tenantId: string;
  tenantName?: string;
  payeeId: string;
  payeeName?: string;
  locationId: string;
  locationName?: string;
  frequency: string;
  scheduleTime: string;
  timezone: string;
  daysOfWeek?: string | null;
  daysOfMonth?: string | null;
  purpose?: string | null;
  createdByName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastRun?: AutoPoConfigRun | null;
  recentRuns?: AutoPoConfigRun[];
}

export interface AutoPoConfigRun {
  id: string;
  configId: string;
  status: string;
  purchaseOrderId?: string | null;
  orderNumber?: string | null;
  itemsCount: number;
  skipReason?: string | null;
  errorMessage?: string | null;
  startedAt: string;
  finishedAt?: string | null;
}

export interface AutoPoPayee {
  id: string;
  name: string;
  email?: string | null;
  vendorType?: string | null;
}

export interface AutoPoLocation {
  id: string;
  name: string;
  isActive: boolean;
  // Onboarding representative's email for this specific location (tenant_id +
  // location_id), null when that location never completed onboarding or the
  // field was left blank. Only consumed today by the CC-Funding Drive Sync
  // merchant-mapping modal, to disambiguate same-tenant locations.
  repEmail: string | null;
}

export interface CreateAutoPoConfigInput {
  tenantId: string;
  payeeId: string;
  locationId: string;
  frequency: string;
  scheduleTime: string;
  timezone: string;
  daysOfWeek?: string | null;
  daysOfMonth?: string | null;
  purpose?: string | null;
  createdByName: string;
  isActive?: boolean;
}

export interface UpdateAutoPoConfigInput {
  id: string;
  tenantId?: string;
  payeeId?: string;
  locationId?: string;
  frequency?: string;
  scheduleTime?: string;
  timezone?: string;
  daysOfWeek?: string | null;
  daysOfMonth?: string | null;
  purpose?: string | null;
  createdByName?: string;
  isActive?: boolean;
}

// ── Query Keys ─────────────────────────────────────────────────────────────────

const AUTO_PO_CONFIGS_KEY = 'admin-auto-po-configs';

// ── Queries ────────────────────────────────────────────────────────────────────

export function useAutoPoConfigs(params?: { tenantId?: string; page?: number; perPage?: number }) {
  return useQuery({
    queryKey: [AUTO_PO_CONFIGS_KEY, params],
    queryFn: () =>
      api.get<{ data: AutoPoConfig[]; pagination: any }>(
        '/admin/auto-po/configs',
        params as Record<string, string | number | boolean | undefined>,
      ),
  });
}

export function useAutoPoConfig(id: string) {
  return useQuery({
    queryKey: [AUTO_PO_CONFIGS_KEY, id],
    queryFn: () => api.get<{ data: AutoPoConfig }>(`/admin/auto-po/configs/${id}`),
    enabled: !!id,
  });
}

export function useAutoPoConfigRuns(id: string, params?: { page?: number; perPage?: number }) {
  return useQuery({
    queryKey: [AUTO_PO_CONFIGS_KEY, id, 'runs', params],
    queryFn: () =>
      api.get<{ data: AutoPoConfigRun[]; pagination: any }>(
        `/admin/auto-po/configs/${id}/runs`,
        params as Record<string, string | number | boolean | undefined>,
      ),
    enabled: !!id,
  });
}

export function useTenantPayeesForAutoPo(tenantId: string) {
  return useQuery({
    queryKey: [AUTO_PO_CONFIGS_KEY, 'tenants', tenantId, 'payees'],
    queryFn: () => api.get<{ data: AutoPoPayee[] }>(`/admin/auto-po/tenants/${tenantId}/payees`),
    enabled: !!tenantId,
  });
}

export function useTenantLocationsForAutoPo(tenantId: string) {
  return useQuery({
    queryKey: [AUTO_PO_CONFIGS_KEY, 'tenants', tenantId, 'locations'],
    queryFn: () => api.get<{ data: AutoPoLocation[] }>(`/admin/auto-po/tenants/${tenantId}/locations`),
    enabled: !!tenantId,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────

export function useCreateAutoPoConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAutoPoConfigInput) => api.post<{ data: AutoPoConfig }>('/admin/auto-po/configs', data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [AUTO_PO_CONFIGS_KEY] });
    },
  });
}

export function useUpdateAutoPoConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: UpdateAutoPoConfigInput) =>
      api.put<{ data: AutoPoConfig }>(`/admin/auto-po/configs/${id}`, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [AUTO_PO_CONFIGS_KEY] });
    },
  });
}

export function useDeleteAutoPoConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ data: { deleted: boolean } }>(`/admin/auto-po/configs/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [AUTO_PO_CONFIGS_KEY] });
    },
  });
}

export function useTriggerAutoPoConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ data: AutoPoConfigRun }>(`/admin/auto-po/configs/${id}/trigger`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [AUTO_PO_CONFIGS_KEY] });
    },
  });
}
